/**
 * Helper function to parse prevalence string to number
 * @param prevalence - Prevalence string (e.g., "1/1000" or "0.001")
 * @returns Parsed prevalence as number
 */
export const parsePrevalence = (prevalence: string): number => {
  if (!prevalence) {
    return 0;
  }

  // remove whitespace
  const cleanedPrevalence = prevalence.replace(/\s+/g, '');

  // handle format like "1-9/1000000" (range format)
  const rangeMatch = cleanedPrevalence.match(/(\d+)-(\d+)\/(\d+)/);
  if (rangeMatch) {
    // use second number in the range as the upper bound
    // e.g., "1-9/1000000" -> 9/1000000
    const upperNumerator = parseFloat(rangeMatch[2]);
    const denominator = parseFloat(rangeMatch[3]);
    return upperNumerator / denominator;
  }

  // Handle formats like "1/1000" or "1 / 1000"
  const simpleMatch = cleanedPrevalence.match(/(\d+)\s*\/\s*(\d+)/);
  if (simpleMatch) {
    const numerator = parseFloat(simpleMatch[1]);
    const denominator = parseFloat(simpleMatch[2]);
    return numerator / denominator;
  }

  // Handle decimal format
  const parsed = parseFloat(cleanedPrevalence);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Calculate factorial (n!)
 * @param n - Non-negative integer
 * @returns Factorial of n
 */
export const factorial = (n: number): number => {
  if (n < 0) {
    return 0;
  }
  if (n === 0 || n === 1) {
    return 1;
  }

  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  return result;
};

/**
 * Calculate the inverse of Poisson cumulative distribution function (Poisson.ppf)
 * This is an approximation using the normal approximation to Poisson
 * @param p - Probability (0.95 for 95% confidence)
 * @param lambda - Mean of the Poisson distribution (population_size × maxc_af)
 * @returns The inverse value rounded to nearest integer
 */
export const poissonInverse = (p: number, lambda: number): number => {
  if (lambda <= 0) {
    return 0;
  }

  // For large lambda, use normal approximation
  if (lambda > 10) {
    // Normal approximation: X ~ N(lambda, lambda)
    // P(X <= x) = P(Z <= (x - lambda) / sqrt(lambda)) = p
    // So x = lambda + z * sqrt(lambda) where z is the inverse normal
    const z = 1.6449; // Approximate z-score for p = 0.95
    const result = lambda + z * Math.sqrt(lambda);
    return Math.round(result);
  }

  // For small lambda, use direct calculation
  // This is a simplified approximation for small values
  let sum = 0;
  let k = 0;
  const maxK = Math.ceil(lambda * 10); // Reasonable upper bound

  while (k <= maxK) {
    // Poisson PMF: P(X = k) = (λ^k * e^(-λ)) / k!
    const pmf = (Math.pow(lambda, k) * Math.exp(-lambda)) / factorial(k);
    sum += pmf;

    if (sum >= p) {
      return k;
    }
    k++;
  }

  return Math.round(lambda);
};

/**
 * Calculate estimated allele frequency based on prevalence ranges and inheritance pattern
 * Uses a binning approach when parameters for maxc_af calculation are not available
 *
 * @param inheritance - Inheritance pattern ('monoallelic' or 'biallelic')
 * @param prevalence - Disease prevalence as a string (e.g., "1/1000")
 * @returns Estimated allele frequency or 0 if inputs are invalid
 */
export const calculateEstimatedAf = (
  inheritance: string,
  prevalence: string,
): number => {
  if (!inheritance || !prevalence) {
    return 0;
  }

  const prevalenceValue = parsePrevalence(prevalence);

  if (prevalenceValue <= 0) {
    return 0;
  }

  // Prevalence thresholds and corresponding estimated_af values
  const monoallelicThresholds = [
    { min: 1 / 1000, max: Infinity, value: 0.001 },
    { min: 1 / 5000, max: 1 / 1000, value: 0.0002 },
    { min: 1 / 10000, max: 1 / 5000, value: 0.0001 },
    { min: 1 / 50000, max: 1 / 10000, value: 0.00002 },
    { min: 1 / 100000, max: 1 / 50000, value: 0.00001 },
    { min: 1 / 500000, max: 1 / 100000, value: 0.000002 },
    { min: 1 / 1000000, max: 1 / 500000, value: 0.000001 },
  ];

  const biallelicThresholds = [
    { min: 1 / 1000, max: Infinity, value: 0.0447 },
    { min: 1 / 5000, max: 1 / 1000, value: 0.02 },
    { min: 1 / 10000, max: 1 / 5000, value: 0.0141 },
    { min: 1 / 50000, max: 1 / 10000, value: 0.00632 },
    { min: 1 / 100000, max: 1 / 50000, value: 0.00447 },
    { min: 1 / 500000, max: 1 / 100000, value: 0.002 },
    { min: 1 / 1000000, max: 1 / 500000, value: 0.00141 },
  ];

  const thresholds =
    inheritance === 'monoallelic' ? monoallelicThresholds : biallelicThresholds;

  // Find the appropriate threshold range
  for (const threshold of thresholds) {
    if (prevalenceValue >= threshold.min && prevalenceValue < threshold.max) {
      return threshold.value;
    }
  }

  // If prevalence is below the lowest threshold, return the lowest value
  const lowestThreshold = thresholds[thresholds.length - 1];
  if (prevalenceValue < lowestThreshold.min) {
    return lowestThreshold.value;
  }

  return 0;
};

/**
 * Calculate maximum credible allele frequency (maxc_af) based on inheritance pattern and form values
 *
 * For monoallelic (autosomal dominant) inheritance:
 * maxc_af = prevalence × allelic_heterogeneity × genetic_heterogeneity × (1/penetrance) × 0.5
 *
 * For biallelic (autosomal recessive) inheritance:
 * maxc_af = √prevalence × allelic_heterogeneity × √genetic_heterogeneity × (1/√penetrance)
 *
 * @param values - Form values containing inheritance pattern and calculation parameters
 * @returns Calculated maxc_af value or 0 if inputs are invalid
 */
export const calculateMaxcAf = (values: {
  inheritance?: string;
  prevalence?: string;
  allelic_heterogeneity?: number;
  genetic_heterogeneity?: number;
  penetrance?: number;
}): number => {
  const {
    inheritance,
    prevalence,
    allelic_heterogeneity = 0,
    genetic_heterogeneity = 0,
    penetrance = 0,
  } = values;

  // Parse prevalence to number
  const prevalenceValue = parsePrevalence(prevalence || '');

  // Convert penetrance from percentage to decimal (e.g., 25% -> 0.25)
  const penetranceValue = penetrance / 100;

  // Return 0 if any required value is missing or invalid
  if (
    !inheritance ||
    !prevalenceValue ||
    !allelic_heterogeneity ||
    !genetic_heterogeneity ||
    !penetranceValue ||
    penetranceValue === 0
  ) {
    return 0;
  }

  let maxcAf = 0;

  if (inheritance === 'monoallelic') {
    // For monoallelic inheritance:
    // maxc_af = prevalence × allelic_heterogeneity × genetic_heterogeneity × (1/penetrance) × 0.5
    maxcAf =
      prevalenceValue *
      allelic_heterogeneity *
      genetic_heterogeneity *
      (1 / penetranceValue) *
      0.5;
  } else if (inheritance === 'biallelic') {
    // For autosomal recessive (biallelic) inheritance:
    // maxc_af = √prevalence × allelic_heterogeneity × √genetic_heterogeneity × (1/√penetrance)
    if (genetic_heterogeneity === 0) {
      return 0;
    }
    maxcAf =
      Math.sqrt(prevalenceValue) *
      allelic_heterogeneity *
      Math.sqrt(genetic_heterogeneity) *
      (1 / Math.sqrt(penetranceValue));
  }

  return maxcAf;
};

/**
 * Calculate maximum tolerated reference allele count (maxc_ac) using Poisson distribution
 *
 * maxc_ac = Poisson.ppf(0.95, population_size × maxc_af)
 * Where:
 * - Poisson.ppf: Inverse of Poisson cumulative distribution function
 * - 0.95: Confidence level (95%)
 * - population_size: Total number of alleles sampled
 * - maxc_af: Maximum credible allele frequency calculated above
 *
 * @param values - Form values containing calculation parameters
 * @param populationSize - Total population size
 * @returns Calculated maxc_ac value or 0 if inputs are invalid
 */
export const calculateMaxcAc = (
  values: {
    inheritance?: string;
    prevalence?: string;
    allelic_heterogeneity?: number;
    genetic_heterogeneity?: number;
    penetrance?: number;
  },
  populationSize: number,
): number => {
  const maxcAf = calculateMaxcAf(values);

  if (maxcAf <= 0 || populationSize <= 0) {
    return 0;
  }

  // Calculate lambda = population_size × maxc_af
  const lambda = populationSize * maxcAf;

  // Calculate maxc_ac = Poisson.ppf(0.95, lambda)
  const maxcAc = poissonInverse(0.95, lambda);

  return maxcAc;
};
